import { toast } from '@/design-system/toaster';
import { ToastOptions } from '@/types/components';
import { ToastType } from '@/types/common';

/**
 * Global toast API — same signatures as before, now backed by the
 * design-system Toaster (sonner). The kit `<Toaster />` is mounted once in
 * App.tsx; react-toastify is no longer used.
 *
 * ToastOptions is kept for call-site compatibility. Options with a sonner
 * equivalent (position, autoClose→duration, toastId→id) are mapped; the
 * react-toastify-only flags (theme, draggable, progress bar…) are no-ops.
 */

let lastCall = 0;

export const showToast = (
  type: ToastType,
  title: string,
  message: string,
  options?: Partial<ToastOptions>
): void => {
  const now = Date.now();

  // Global throttle — calls within 100ms of any prior toast are dropped.
  if (now - lastCall < 100) {
    return;
  }
  lastCall = now;

  if (!type || typeof toast[type] !== 'function') {
    return;
  }

  const safeTitle = title || 'Notification';
  const safeMessage = message || 'No message provided';

  try {
    toast[type](safeTitle, {
      description: safeMessage,
      id: options?.toastId ?? `toast-${now}`,
      position: options?.position,
      duration:
        options?.autoClose === false
          ? Infinity
          : typeof options?.autoClose === 'number'
            ? options.autoClose
            : undefined,
    });
  } catch (error) {
    try {
      toast[type](`${safeTitle}: ${safeMessage}`, { id: `fallback-${now}` });
    } catch (fallbackError) {}
  }
};

export const showSuccessToast = (
  title: string,
  message: string,
  options?: Partial<ToastOptions>
): void => {
  showToast('success', title, message, options);
};

export const showErrorToast = (
  title: string,
  message: string,
  options?: Partial<ToastOptions>
): void => {
  showToast('error', title, message, options);
};

export const showWarningToast = (
  title: string,
  message: string,
  options?: Partial<ToastOptions>
): void => {
  showToast('warning', title, message, options);
};

export const showInfoToast = (
  title: string,
  message: string,
  options?: Partial<ToastOptions>
): void => {
  showToast('info', title, message, options);
};

export const cleanupToasts = (): void => {
  try {
    toast.dismiss();
  } catch (error) {}
};

const GlobalToast = {
  showToast,
  showSuccessToast,
  showErrorToast,
  showWarningToast,
  showInfoToast,
  cleanupToasts,
};

export default GlobalToast;
